import React, { createContext, useContext, useReducer } from "react";
import { MapState } from "types/MapState";
import { MarkerState } from "types/MarkerState";
import { MapStyle } from "types/MapStyle";

type MapAction =
	| { type: "SET_STYLE"; payload: MapStyle }
	| { type: "SET_CENTER"; payload: [number, number] }
	| { type: "SET_ZOOM"; payload: number }
	| { type: "TOGGLE_LAYER"; payload: string }
	| { type: "ADD_MARKER"; payload: MarkerState }
	| { type: "REMOVE_MARKER"; payload: string };

const initialState: MapState = {
	style: "streets",
	center: [-73.935242, 40.73061], // New York City
	zoom: 13,
	layers: [
		{
			id: "map-style",
			isActive: true,
			type: "base",
			data: { style: "streets" },
		},
		{ id: "personal-spaces", isActive: false, type: "overlay" },
		{ id: "healthcare", isActive: false, type: "overlay" },
		{ id: "food-nutrition", isActive: false, type: "overlay" },
		{ id: "education", isActive: false, type: "overlay" },
		{ id: "community", isActive: false, type: "overlay" },
		{ id: "transportation", isActive: false, type: "overlay" },
		{ id: "housing", isActive: false, type: "overlay" },
		{ id: "economic", isActive: false, type: "overlay" },
	],
	markers: [],
};

const MapContext = createContext<
	| {
			state: MapState;
			toggleLayer: (layerId: string) => void;
			updateMapStyle: (style: MapStyle) => void;
			setCenter: (center: [number, number]) => void;
			setZoom: (zoom: number) => void;
			addMarker: (marker: MarkerState) => void;
			removeMarker: (markerId: string) => void;
	  }
	| undefined
>(undefined);

function mapReducer(state: MapState, action: MapAction): MapState {
	switch (action.type) {
		case "SET_STYLE":
			return {
				...state,
				style: action.payload,
			};
		case "SET_CENTER":
			return {
				...state,
				center: action.payload,
			};
		case "SET_ZOOM":
			return {
				...state,
				zoom: action.payload,
			};
		case "TOGGLE_LAYER":
			return {
				...state,
				layers: state.layers.map((layer) =>
					layer.id === action.payload
						? { ...layer, isActive: !layer.isActive }
						: layer
				),
			};
		case "ADD_MARKER":
			return {
				...state,
				markers: [...state.markers, action.payload],
			};
		case "REMOVE_MARKER":
			return {
				...state,
				markers: state.markers.filter(
					(marker: MarkerState) => marker.id !== action.payload
				),
			};
		default:
			return state;
	}
}

export function MapProvider({ children }: { children: React.ReactNode }) {
	const [state, dispatch] = useReducer(mapReducer, initialState);

	const toggleLayer = (layerId: string) => {
		dispatch({ type: "TOGGLE_LAYER", payload: layerId });
	};

	const updateMapStyle = (style: MapStyle) => {
		dispatch({ type: "SET_STYLE", payload: style });
	};

	const setCenter = (center: [number, number]) => {
		dispatch({ type: "SET_CENTER", payload: center });
	};

	const setZoom = (zoom: number) => {
		dispatch({ type: "SET_ZOOM", payload: zoom });
	};

	const addMarker = (marker: MarkerState) => {
		dispatch({ type: "ADD_MARKER", payload: marker });
	};

	const removeMarker = (markerId: string) => {
		dispatch({ type: "REMOVE_MARKER", payload: markerId });
	};

	return (
		<MapContext.Provider
			value={{
				state,
				toggleLayer,
				updateMapStyle,
				setCenter,
				setZoom,
				addMarker,
				removeMarker,
			}}
		>
			{children}
		</MapContext.Provider>
	);
}

export function useMapContext() {
	const context = useContext(MapContext);
	if (context === undefined) {
		throw new Error("useMapContext must be used within a MapProvider");
	}
	return context;
}
